import { Check, Clear, CompareArrows, History, ReportProblem, VisibilityOff } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Breadcrumbs, Button, FormControl, IconButton, InputLabel, Link, MenuItem, Paper, Select, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Tooltip, Typography } from "@mui/material";
import { useAtom } from 'jotai';
import { ChangeEvent, FC, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { modalDivergences, modalIgnoreDivergences, permissionsAtom, userTypeAtom } from '../../atoms/atom';
import AppBar from "../../components/app-bar/AppBar";
import ModalDivergences from '../../components/comparation/ModalDivergences';
import ModalIgnoreDivergences from '../../components/comparation/ModalIgnoreDivergences';
import { ExecutionService } from "../../services/ExecutionService";
import { Comparation } from '../../types/Comparation';
import { getComparationFilters } from '../../types/ComparationFilters';
import { ComparationsDTO } from "../../types/ComparationsDTO";
import { useUtils } from "../../utils/utilFunctions";
import { getLoading } from '../execution-events/ExecutionEvents';
import './Comparations.scss';

const Comparations: FC = () => {
	const { id } = useParams<{ id: string }>();
	const [comparations, setComparations] = useState<ComparationsDTO | null>(null);
	const [expandedCompany, setExpandedCompany] = useState<string | false>('');
	const [filter, setFilter] = useState('SOME');
	const [search, setSearch] = useState('');
	const [selectedComparation, setSelectedComparation] = useState<Comparation>();
	const [tab, setTab] = useState<string | undefined>();
	const [loading, setLoading] = useState(true);
	const [permissions] = useAtom(permissionsAtom);
	const [, setOpenDivergences] = useAtom(modalDivergences);
	const [, setOpenIgnoreDivergences] = useAtom(modalIgnoreDivergences);
	const [userType] = useAtom(userTypeAtom);
	const isMounted = useRef(true);
	const executionService = new ExecutionService();
	const { verifyPermission } = useUtils();
	const navigate = useNavigate();

	const selected = comparations?.executions.find(execution => execution.executionDate === tab);
	const externalUser = "EXTERNO" === userType;
	const canManageComparations = permissions.includes('MANAGE_COMPARATIONS');

	const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
		setExpandedCompany(isExpanded ? panel : false);
	};

	const back = () => {
		navigate(-1);
	}

	const handleChangeSearch = (event: ChangeEvent<HTMLInputElement>) => {
		setSearch(event.target.value);
	}

	const handleDivergences = (comparation: Comparation) => {
		setSelectedComparation(comparation);
		setOpenDivergences(true);
	}

	const handleIgnoreDivergences = (comparation: Comparation) => {
		setSelectedComparation(comparation);
		setOpenIgnoreDivergences(true);
	}

	const loadComparations = async () => {
		const result = await executionService.getExecutionById(id);
		if (result) {
			setComparations(result.data);
			setTab(result.data?.executions[result.data.executions.length - 1].executionDate);
		}
		setLoading(false);
	};

	useEffect(() => {
		loadComparations();
	}, [id]);

	useEffect(() => {
		if (!isMounted.current) return;
		if (!!permissions.length) {
			verifyPermission(permissions, 'EXECUTIONS');
		}
	}, []);

	return(
		<div className="comparations">
			<AppBar
				modulo="Comparações"
			/>
			<div className="component">
				<div className="top">
					<div className="etapas">
						<Breadcrumbs aria-label="breadcrumb" sx={{display: "flex", alignItems: "flex-end"}}>
							<Link variant="h6" sx={{cursor: "pointer"}} underline="hover" color="inherit" onClick={() => back()}>
								Execuções
							</Link>
							<Link
								variant="subtitle1"
								underline="hover"
								color="text.primary"
								aria-current="page"
								sx={{display: "flex", alignItems: "flex-end !important"}}
							>
								{comparations?.companyName}
							</Link>
						</Breadcrumbs>
					</div>
					<div className="search">
						{canManageComparations && <Button
							variant="contained"
							color="secondary"
							startIcon={<History/>}
							onClick={() => navigate(`/execucoes/${id}/comparacoes/auditoria`)}
						>
							Auditoria de atualizações
						</Button>}
						<TextField
							sx={{width: '305px'}}
							label="Buscar funcionário pelo nome ou CPF"
							id="employee-search"
							size="small"
							value={search}
							onChange={handleChangeSearch}
						/>
						<FormControl
							sx={{width: "250px"}}
							size='small'
						>
							<InputLabel>
								Filtrar funcionários
							</InputLabel>
							<Select
							label='Filtrar funcionários'
								id="select-categoria"
								value={filter}
								onChange={(e) => setFilter(e.target.value)}
							>
								{getComparationFilters().map(item => (
									<MenuItem
										key={item.id}
										value={item.id}
									>
										{item.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</div>
				</div>
				<div className="tabela">
					<Tabs
						value={tab}
						onChange={(_, newTab) => setTab(newTab)}
						textColor="secondary"
					>
						{(comparations?.executions || []).map(filial => (
							<Tab label={filial.executionDate} value={filial.executionDate} />
						))}
					</Tabs>
					<Typography
						sx={{display: selected?.totalEsocial ? "" : "none"}}
						color="#e5e5e5" variant='h6'
					>
						Total de trabalhadores no eSocial: {selected?.totalEsocial}
					</Typography>
					<div className="listagem">
						{!comparations?.executions.some(execution => execution.executionCompanies.some(company =>
							filterComparations(company.comparations).length)) && !loading
							? <div className="sem-execucoes">Nenhum registro encontrado para o filtro aplicado...</div>
							: loading
								? getLoading()
								: <></>
						}
						{selected?.executionCompanies.map(company => (
							filterComparations(company.comparations).length
								? <Accordion
									expanded={expandedCompany === company.companyName}
									onChange={handleChange(company.companyName)}
									slotProps={{ transition: { unmountOnExit: true } }}
								>
									<AccordionSummary
										expandIcon={<ExpandMoreIcon />}
										aria-controls={company.companyName}
										id={company.companyName}
										sx={{display: "flex", justifyContent: "space-between"}}
									>
										<Typography variant='h6'>{company.companyName}</Typography>
										<Typography
											color={filterComparations(company.comparations).filter(c => c.someDivergence).length ? 'error' : 'springgreen'}
											variant='h6'
											sx={{marginRight: "25vh"}}
										>
											{
												filterComparations(company.comparations).filter(c => c.someDivergence).length
												? filterComparations(company.comparations).filter(c => c.someDivergence).length + ' com divergência'
												: 'Nenhuma divergência'
											}
										</Typography>
									</AccordionSummary>
									<AccordionDetails>
										<TableContainer sx={{ maxHeight: 700, boxShadow: "0px 0px 1px 1px #b6b6b6" }} component={Paper}>
											<Table stickyHeader sx={{ minWidth: 650 }} aria-label="simple table">
												<TableHead>
												<TableRow>
													<TableCell sx={{fontSize: "1.6vh"}}>Funcionário</TableCell>
													<TableCell className='th-styled'>Documento</TableCell>
													<TableCell className='th-styled'>Nome</TableCell>
													<TableCell className='th-styled'>Cargo</TableCell>
													<TableCell className='th-styled'>CPF</TableCell>
													<TableCell className='th-styled'>Data de admissão</TableCell>
													<TableCell className='th-styled'>Data de desligamento</TableCell>
													<TableCell className='th-styled'>Data de nascimento</TableCell>
													<TableCell className='th-styled'>CBO</TableCell>
													<TableCell className='th-styled'>Local de trabalho</TableCell>
													<TableCell className='th-styled'>Matrícula</TableCell>
													<TableCell className='th-styled'>Situação</TableCell>
													<TableCell className='th-styled'>Não está no eSocial</TableCell>
													<TableCell className='th-styled'>Não está no SGG</TableCell>
													<TableCell className='th-styled'>Admissão preliminar</TableCell>
													<TableCell className='th-styled'></TableCell>
												</TableRow>
												</TableHead>
												<TableBody>
												{filterComparations(company.comparations).map((comparation) => (
													<TableRow
														key={comparation.name}
														sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
														className='row-line'
													>
														<TableCell component="th" scope="row">{verifyUnlinked(comparation)}</TableCell>
														<TableCell component="th" scope="row" sx={{width: "140px"}}>{comparation.cpf}</TableCell>
														{comparation.updatedBySync
															? <TableCell className='updated-by-sync' colSpan={13}>
																Cadastro alterado mediante atualização - é necessário realizar uma nova extração
																para avaliar o estado atual
															</TableCell>
															: <>
																<TableCell className='th-styled'>{icon(comparation, comparation.divergentNome)}</TableCell>
																<TableCell className='th-styled'>{icon(comparation, comparation.divergentCargo)}</TableCell>
																<TableCell className='th-styled'>{icon(comparation, comparation.divergentCpf)}</TableCell>
																<TableCell className='th-styled'>{icon(comparation, comparation.divergentDataAdmissao)}</TableCell>
																<TableCell className='th-styled'>{icon(comparation, comparation.divergentDataDesligamento)}</TableCell>
																<TableCell className='th-styled'>{icon(comparation, comparation.divergentDataNascimento)}</TableCell>
																<TableCell className='th-styled'>{icon(comparation, comparation.divergentDescricaoCbo)}</TableCell>
																<TableCell className='th-styled'>{icon(comparation, comparation.divergentLocalTrabalho)}</TableCell>
																<TableCell className='th-styled'>{icon(comparation, comparation.divergentMatricula)}</TableCell>
																<TableCell className='th-styled'>{icon(comparation, comparation.divergentSituacao)}</TableCell>
																<TableCell className='th-styled'>{isFound(comparation.notFound)}</TableCell>
																<TableCell className='th-styled'>{isFound(comparation.notSGG)}</TableCell>
																<TableCell className='th-styled'>{isPreliminar(comparation)}</TableCell>
															</>
														}
														<TableCell className='th-styled'>
															{comparation.error
																? <Tooltip title={comparation.error}>
																		<ReportProblem color='error'/>
																	</Tooltip>
																: comparation.updatedBySync || externalUser || !canManageComparations
																	? <></>
																	: <div className="acoes">
																		<Tooltip title="Atualizar divergências no SGG">
																			<IconButton
																				color="inherit"
																				onClick={() => handleDivergences(comparation)}
																				disabled={!comparation.esocialValues}
																			>
																				<CompareArrows
																					sx={{
																						fontSize: '3vh',
																						color: comparation.esocialValues ? "#0085FF" : "#4e4e4eff"
																					}}
																				/>
																			</IconButton>
																		</Tooltip>
																		<Tooltip title="Ignorar divergências">
																			<IconButton
																				color="inherit"
																				onClick={() => handleIgnoreDivergences(comparation)}
																				disabled={!comparation.esocialValues}
																			>
																				<VisibilityOff
																					sx={{
																						fontSize: '3vh',
																						color: comparation.esocialValues ? "#dadd3eff" : "#4e4e4eff"
																					}}
																				/>
																			</IconButton>
																		</Tooltip>
																	</div>
															}
														</TableCell>
													</TableRow>
												))}
												</TableBody>
											</Table>
										</TableContainer>
									</AccordionDetails>
								</Accordion>
								: <></>
						))}
					</div>
				</div>
			</div>
			<ModalDivergences comparation={selectedComparation} onSynced={loadComparations}/>
			<ModalIgnoreDivergences comparation={selectedComparation} onIgnored={loadComparations}/>
		</div>
	);

	function icon(comparation: Comparation, value: boolean) {
		if (comparation.notFound || comparation.preliminarAdmission || comparation.notSGG) {
			return <></>;
		}
		return value
			? <Clear sx={{fontSize: '4vh'}} color='error'/> : <Check sx={{fontSize: '4vh'}} color='success'/>;
	}

	function isFound(value: boolean) {
		return value ? <Clear sx={{fontSize: '4vh'}} color='error'/> : <></>;
	}

	function isPreliminar(comparation: Comparation) {
		return comparation.preliminarAdmission && !comparation.notFound
			? <Check sx={{fontSize: '4vh'}} color='success'/> : <></>
	}

	function verifyUnlinked(comparation: Comparation) {
		return <Typography sx={{display: "flex", gap: "10px"}}>
			{comparation.name}{comparation.unlinked ? <Typography color="#dadd3eff">(sem vínculo)</Typography> : ""}
		</Typography>;
	}

	function filterComparations(comparations: Comparation[]) {
		let filtered = comparations;
		switch (filter) {
			case "SOME":
				filtered = comparations.filter(item => item.someDivergence);
				break;
			case "ESOCIAL":
				filtered = comparations.filter(item => item.notFound);
				break;
			case "NAME":
				filtered = comparations.filter(item => item.divergentNome);
				break;
			case "CARGO":
				filtered = comparations.filter(item => item.divergentCargo);
				break;
			case "CPF":
				filtered = comparations.filter(item => item.divergentCpf);
				break;
			case "ADMISSION":
				filtered = comparations.filter(item => item.divergentDataAdmissao);
				break;
			case "OFF":
				filtered = comparations.filter(item => item.divergentDataDesligamento);
				break;
			case "BIRTHDAY":
				filtered = comparations.filter(item => item.divergentDataNascimento);
				break;
			case "CBO":
				filtered = comparations.filter(item => item.divergentDescricaoCbo);
				break;
			case "WORKSPACE":
				filtered = comparations.filter(item => item.divergentLocalTrabalho);
				break;
			case "MATRICULA":
				filtered = comparations.filter(item => item.divergentMatricula);
				break;
			case "SITUATION":
				filtered = comparations.filter(item => item.divergentSituacao);
				break;
			case "PRELIMINAR":
				filtered = comparations.filter(item => item.preliminarAdmission);
				break;
			default:
				filtered = comparations;
		}
		if (search && search.trim() !== "") {
			const term = search.toLowerCase();
			filtered = filtered
				.filter(item => item.name.toLowerCase().includes(term) || item.cpf.toLowerCase().includes(term));
		}
		return filtered;
	}
}

export default Comparations;